import { joiResolver } from '@hookform/resolvers/joi';


import { makeStyles } from 'tss-react/mui';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ApiButton,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  ErrorInfo,
  useSnackbarHandler,
} from 'web-library';
import { usePostExamFee } from '../../../../api-refactored/exam-configuration/usePostExamFee';
import {
  ExamFeeModelResponse
} from '../../../../api-refactored/exam-configuration/usePostExamFeeListing';
import { usePutExamFee } from '../../../../api-refactored/exam-configuration/usePutExamFee';
import GstFormSection from './GstFormSection';
import RegistrationFeeFormSection from './RegistrationFeeFormSection';
import { SpersExamFeeFormData, SpersExamFeeSchema } from './schema/SpersExamFeeSchema';
import TimerFeeFormSection from './TimerFeeFormSection';

const useStyles = makeStyles()((theme) =>
({
  actionContainer: {
    paddingRight: theme.spacing(1),
    paddingBottom: theme.spacing(2),
  },
  title: {
    color: theme.color.text.textPrimary,
    paddingBottom: theme.spacing(3),
  },
  formFieldContainer: {
    paddingBottom: theme.spacing(3),
  },
  dividerContainer: {
    paddingBottom: theme.spacing(3),
  },
}));

interface SpersExamFeeModalProps {
  registrationExamLevelUuid: string;
  year: number;
  isEditState: boolean;
  isModalOpened: boolean;
  setModalOpened: Dispatch<SetStateAction<boolean>>;
  fetchExamFeeListing: () => void;
  defaultData?: ExamFeeModelResponse;
}

const SpersExamFeeModal = ({
  registrationExamLevelUuid,
  year,
  isEditState,
  isModalOpened,
  setModalOpened,
  fetchExamFeeListing,
  defaultData,
}: SpersExamFeeModalProps) => {
  const { t } = useTranslation();
  const { snackbarHandler } = useSnackbarHandler();
  const { classes } = useStyles();
  const [apiErrorMsg, setApiErrorMsg] = useState<string | null>(null);

  const getFormDefaultValue = () => {
    let defaultGstInclusive = 'Y';
    if (defaultData) {
      defaultGstInclusive = defaultData.gstInclusive ? 'Y' : 'N';
    }
    return {
      gstPercent: defaultData?.gstPercent,
      gstInclusive: defaultGstInclusive,
      registrationFee: defaultData?.registrationFee,
      lateRegistrationFee: defaultData?.lateRegistrationFee,
      firstTimerRegistrationFee: defaultData?.firstTimerRegistrationFee,
      secondTimerRegistrationFee: defaultData?.secondTimerRegistrationFee,
      firstTimerLateFee: defaultData?.firstTimerLateFee,
      secondTimerLateFee: defaultData?.secondTimerLateFee,
    };
  };

  const formHook = useForm<SpersExamFeeFormData>({
    mode: 'onSubmit',
    resolver: joiResolver(SpersExamFeeSchema),
    defaultValues: getFormDefaultValue(),
  });
  const { handleSubmit, reset: formReset } = formHook;

  const {
    mutate: createExamFeeMutate,
    reset: createExamFeeReset,
    status: createExamFeeStatus,
  } = usePostExamFee({
    onSuccess: () => {
      fetchExamFeeListing();
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'success',
        targetData: t('exam->>ExamFeeConfiguration.SpersFeeAdded'),
      });
      onDialogCloseHandler();
    },
    onError: (err) => {
      setApiErrorMsg(err.response?.data.message!);
    },
  });
  const {
    mutate: updateExamFeeMutate,
    reset: updateExamFeeReset,
    status: updateExamFeeStatus,
  } = usePutExamFee({
    onSuccess: () => {
      fetchExamFeeListing();
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'success',
        targetData: t('exam->>ExamFeeConfiguration.SpersUpdated'),
      });
      onDialogCloseHandler();
    },
    onError: (err) => {
      setApiErrorMsg(err.response?.data.message!);
    },
  });

  useEffect(() => {
    // Update form default value each time the modal is opened.
    if (isModalOpened) {
      formReset(getFormDefaultValue());
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [isModalOpened]);

  const onDialogCloseHandler = () => {
    setModalOpened(false);
    formReset();
    createExamFeeReset();
    updateExamFeeReset();
    setApiErrorMsg(null);
  };

  const onSubmitHandler = (formData: SpersExamFeeFormData) => {
    setApiErrorMsg(null);
    const baseRequest = {
      gstInclusive: formData.gstInclusive === 'Y',
      gstPercent: formData.gstPercent,
      registrationFee: formData.registrationFee,
      lateRegistrationFee: formData.lateRegistrationFee,
      firstTimerLateFee: formData.firstTimerLateFee,
      secondTimerLateFee: formData.secondTimerLateFee,
      firstTimerRegistrationFee: formData.firstTimerRegistrationFee,
      secondTimerRegistrationFee: formData.secondTimerRegistrationFee,
    };

    if (isEditState) {
      updateExamFeeMutate({ ...baseRequest, examFeeUuid: defaultData?.uuid || '' });
    } else {
      createExamFeeMutate({
        ...baseRequest,
        registrationExamLevelUuid,
        year,
      });
    }
  };

  return (
    <Dialog id="spers-exam-fee-modal" open={isModalOpened} maxWidth="md">
      {isEditState ? (
        <DialogTitle>{t('exam->>ExamFeeConfiguration.EditExamFees')}</DialogTitle>
      ) : (
        <DialogTitle>{t('exam->>ExamFeeConfiguration.AddExamFees')}</DialogTitle>
      )}

      <DialogContent>
        <form onSubmit={handleSubmit(onSubmitHandler)} id="spers-fee-form">
          <TimerFeeFormSection
            title={t('exam->>ExamFeeConfiguration.ScExamFees')}
            formHook={formHook}
            isModalOpened={isModalOpened}
            defaultData={defaultData}
          />
          <Divider className={classes.dividerContainer} />
          <RegistrationFeeFormSection
            title={t('exam->>ExamFeeConfiguration.PrExamFees')}
            formHook={formHook}
            isModalOpened={isModalOpened}
            defaultData={defaultData}
          />
          <Divider className={classes.dividerContainer} />
          <GstFormSection
            formHook={formHook}
            isModalOpened={isModalOpened}
            defaultData={defaultData}
          />
        </form>
        {apiErrorMsg && <ErrorInfo label={apiErrorMsg} />}
      </DialogContent>
      <DialogActions>
        <Box className={classes.actionContainer}>
          <Button
            id="button-spe-cancel-exam-fees"
            type="button"
            variant="text"
            onClick={onDialogCloseHandler}
          >
            {t('exam->>ExamConfiguration.Cancel')}
          </Button>
          <ApiButton
            id="button-spe-confirm-exam-fees"
            type="submit"
            status={isEditState ? updateExamFeeStatus : createExamFeeStatus}
            form="spers-fee-form"
          >
            {t('exam->>ExamFeeConfiguration.ConfirmExamFees')}
          </ApiButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default SpersExamFeeModal;
